import {
  PARTY_PROTOCOL_VERSION,
  PARTY_SOCKET_PATH,
  normalizePartyNickname,
  normalizePartyRoomCode,
  parsePartyServerMessage,
  type PartyClientMessage,
  type PartyGameId,
  type PartyPublicGameId,
  type PartyRoomVisibility,
  type PartyServerMessage,
} from './protocol';

export type PartyConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface PartyNetworkHandlers {
  onConnection(state: PartyConnectionState): void;
  onMessage(message: PartyServerMessage): void;
}

type InitialMessage = Extract<PartyClientMessage, {
  type: 'create' | 'join' | 'list-public' | 'quick-join';
}>;

export function partyPublicOrigin(): string {
  if (window.location.hostname.endsWith('.chatgpt.site')) return 'https://oneultool.com';
  return window.location.origin;
}

function socketUrl(): string {
  const origin = partyPublicOrigin();
  const url = new URL(PARTY_SOCKET_PATH, origin);
  url.protocol = origin.startsWith('https:') ? 'wss:' : 'ws:';
  return url.toString();
}

function tokenKey(gameId: PartyGameId, roomCode: string): string {
  return `party-room-${gameId}-${roomCode}`;
}

function readToken(gameId: PartyGameId, roomCode: string): string | undefined {
  try {
    return window.sessionStorage.getItem(tokenKey(gameId, roomCode)) ?? undefined;
  } catch {
    return undefined;
  }
}

function saveToken(gameId: PartyGameId, roomCode: string, token: string): void {
  try {
    window.sessionStorage.setItem(tokenKey(gameId, roomCode), token);
  } catch {
    // The current socket still works when private browsing blocks storage.
  }
}

function removeToken(gameId: PartyGameId, roomCode: string): void {
  try {
    window.sessionStorage.removeItem(tokenKey(gameId, roomCode));
  } catch {
    // Storage is optional.
  }
}

export class PartyNetwork {
  readonly #gameId: PartyGameId;
  readonly #handlers: PartyNetworkHandlers;
  #socket: WebSocket | null = null;
  #generation = 0;
  #reconnectTimer: number | null = null;
  #reconnectStartedAt = 0;
  #reconnectAttempt = 0;
  #intentionalClose = false;
  #allowReconnect = false;
  #roomCode: string | null = null;
  #token: string | null = null;
  #nickname = '';
  #playerId: string | null = null;
  #sequence = 0;

  constructor(gameId: PartyGameId, handlers: PartyNetworkHandlers) {
    this.#gameId = gameId;
    this.#handlers = handlers;
  }

  get roomCode(): string | null {
    return this.#roomCode;
  }

  get playerId(): string | null {
    return this.#playerId;
  }

  createRoom(nicknameValue: string, visibility: PartyRoomVisibility = 'private'): void {
    const nickname = normalizePartyNickname(nicknameValue);
    if (!nickname) return;
    this.#reset(false);
    this.#nickname = nickname;
    this.#connect({
      v: PARTY_PROTOCOL_VERSION,
      type: 'create',
      gameId: this.#gameId,
      nickname,
      visibility,
    }, false);
  }

  browsePublicRooms(): boolean {
    const gameId = this.#publicGameId();
    if (!gameId || this.#roomCode) return false;
    if (this.#socket?.readyState === WebSocket.OPEN) {
      this.#send({ v: PARTY_PROTOCOL_VERSION, type: 'list-public', gameId });
      return true;
    }
    if (this.#socket?.readyState === WebSocket.CONNECTING) return true;
    this.#reset(false);
    this.#connect({ v: PARTY_PROTOCOL_VERSION, type: 'list-public', gameId }, false);
    return true;
  }

  requestPublicRooms(): boolean {
    return this.browsePublicRooms();
  }

  quickJoin(nicknameValue: string): boolean {
    const gameId = this.#publicGameId();
    const nickname = normalizePartyNickname(nicknameValue);
    if (!gameId || !nickname) return false;
    this.#reset(false);
    this.#nickname = nickname;
    this.#connect({
      v: PARTY_PROTOCOL_VERSION,
      type: 'quick-join',
      gameId,
      nickname,
    }, false);
    return true;
  }

  joinRoom(roomCodeValue: string, nicknameValue: string): void {
    const roomCode = normalizePartyRoomCode(roomCodeValue);
    const nickname = normalizePartyNickname(nicknameValue);
    if (!nickname) return;
    this.#reset(false);
    this.#roomCode = roomCode;
    this.#nickname = nickname;
    const token = readToken(this.#gameId, roomCode);
    this.#token = token ?? null;
    this.#connect({
      v: PARTY_PROTOCOL_VERSION,
      type: 'join',
      gameId: this.#gameId,
      roomCode,
      nickname,
      ...(token ? { token } : {}),
    }, false);
  }

  resumeRoom(roomCodeValue: string, nicknameValue: string): boolean {
    const roomCode = normalizePartyRoomCode(roomCodeValue);
    const nickname = normalizePartyNickname(nicknameValue);
    const token = readToken(this.#gameId, roomCode);
    if (!nickname || !token) return false;
    this.#reset(false);
    this.#roomCode = roomCode;
    this.#nickname = nickname;
    this.#token = token;
    this.#connect({
      v: PARTY_PROTOCOL_VERSION,
      type: 'join',
      gameId: this.#gameId,
      roomCode,
      nickname,
      token,
    }, true);
    return true;
  }

  setReady(ready: boolean): void {
    this.#send({ v: PARTY_PROTOCOL_VERSION, type: 'ready', ready });
  }

  start(): void {
    this.#send({ v: PARTY_PROTOCOL_VERSION, type: 'start' });
  }

  action(action: Readonly<Record<string, unknown>>): void {
    this.#send({
      v: PARTY_PROTOCOL_VERSION,
      type: 'action',
      seq: this.#sequence,
      action,
    });
    this.#sequence += 1;
  }

  rematch(): void {
    this.#send({ v: PARTY_PROTOCOL_VERSION, type: 'rematch' });
  }

  ping(): void {
    this.#send({ v: PARTY_PROTOCOL_VERSION, type: 'ping', clientTime: Date.now() });
  }

  reconnect(): boolean {
    if (!this.#roomCode || !this.#token || !this.#nickname || this.#socket) return false;
    this.#allowReconnect = true;
    this.#reconnectStartedAt = performance.now();
    this.#reconnectAttempt = 0;
    this.#connect({
      v: PARTY_PROTOCOL_VERSION,
      type: 'join',
      gameId: this.#gameId,
      roomCode: this.#roomCode,
      nickname: this.#nickname,
      token: this.#token,
    }, true);
    return true;
  }

  leave(): void {
    const roomCode = this.#roomCode;
    if (this.#socket?.readyState === WebSocket.OPEN) {
      this.#send({ v: PARTY_PROTOCOL_VERSION, type: 'leave' });
    }
    this.#reset(true);
    if (roomCode) removeToken(this.#gameId, roomCode);
  }

  dispose(): void {
    this.#reset(false);
  }

  #connect(initialMessage: InitialMessage, reconnecting: boolean): void {
    const generation = ++this.#generation;
    this.#intentionalClose = false;
    this.#handlers.onConnection(reconnecting ? 'reconnecting' : 'connecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(socketUrl());
    } catch {
      this.#handlers.onConnection('offline');
      return;
    }
    this.#socket = socket;

    socket.addEventListener('open', () => {
      if (generation !== this.#generation) return;
      socket.send(JSON.stringify(initialMessage));
    });
    socket.addEventListener('message', (event) => {
      if (generation !== this.#generation || typeof event.data !== 'string') return;
      const message = parsePartyServerMessage(event.data);
      if (!message) return;
      if (message.type === 'room') {
        this.#roomCode = message.room.roomCode;
        this.#token = message.token;
        this.#playerId = message.playerId;
        this.#sequence = message.nextActionSeq;
        this.#allowReconnect = true;
        this.#reconnectAttempt = 0;
        this.#reconnectStartedAt = 0;
        saveToken(this.#gameId, message.room.roomCode, message.token);
        this.#handlers.onConnection('connected');
      } else if (message.type === 'public-rooms') {
        this.#handlers.onConnection('connected');
      } else if (message.type === 'closed') {
        this.#allowReconnect = false;
        this.#roomCode = null;
        this.#token = null;
        this.#playerId = null;
      } else if (message.type === 'error' && !message.retryable && !this.#playerId) {
        this.#allowReconnect = false;
        this.#handlers.onConnection('offline');
        socket.close(1000, 'request-rejected');
      }
      this.#handlers.onMessage(message);
    });
    socket.addEventListener('close', () => {
      if (generation !== this.#generation) return;
      this.#socket = null;
      if (this.#intentionalClose) return;
      if (this.#allowReconnect && this.#roomCode && this.#token) {
        this.#scheduleReconnect();
      } else {
        this.#handlers.onConnection('offline');
      }
    });
    socket.addEventListener('error', () => {
      // The close event owns retry behavior.
    });
  }

  #scheduleReconnect(): void {
    if (!this.#roomCode || !this.#token || !this.#nickname) return;
    if (this.#reconnectStartedAt === 0) this.#reconnectStartedAt = performance.now();
    if (performance.now() - this.#reconnectStartedAt > 29_000) {
      this.#allowReconnect = false;
      this.#handlers.onConnection('offline');
      return;
    }
    this.#handlers.onConnection('reconnecting');
    const delay = Math.min(3_500, 400 * 2 ** this.#reconnectAttempt);
    this.#reconnectAttempt += 1;
    this.#reconnectTimer = window.setTimeout(() => {
      this.#reconnectTimer = null;
      if (!this.#roomCode || !this.#token) return;
      this.#connect({
        v: PARTY_PROTOCOL_VERSION,
        type: 'join',
        gameId: this.#gameId,
        roomCode: this.#roomCode,
        nickname: this.#nickname,
        token: this.#token,
      }, true);
    }, delay);
  }

  #send(message: PartyClientMessage): void {
    if (this.#socket?.readyState !== WebSocket.OPEN) return;
    this.#socket.send(JSON.stringify(message));
  }

  #publicGameId(): PartyPublicGameId | null {
    return this.#gameId === 'tail-chase' || this.#gameId === 'kong-fortress'
      ? this.#gameId
      : null;
  }

  #reset(reportOffline: boolean): void {
    this.#generation += 1;
    this.#intentionalClose = true;
    this.#allowReconnect = false;
    if (this.#reconnectTimer !== null) window.clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    this.#reconnectStartedAt = 0;
    this.#reconnectAttempt = 0;
    this.#socket?.close();
    this.#socket = null;
    this.#roomCode = null;
    this.#token = null;
    this.#playerId = null;
    this.#sequence = 0;
    if (reportOffline) this.#handlers.onConnection('offline');
  }
}
